import { Component, computed, inject, input, model, output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { SearchChangesInterface } from "@kernel/listing/interfaces/search.interface";
import { PropertyFiltersService } from "@app/core/filtering/services/property-filters.service";

@Component({
  selector: "app-property-search",
  standalone: true,
  templateUrl: "./property-search.component.html",
  styleUrls: ["./property-search.component.scss"],
  imports: [CommonModule, FormsModule, TranslateModule],
})
export class PropertySearchComponent {
  /**
   * Emits when a search is executed or cleared.
   * Used by parent components to refresh results.
   */
  searchApplied = output<SearchChangesInterface | undefined>();

  /** Emits when the user requests to open/close the filters panel. */
  toggleFilters = output<boolean>();

  /** Two-way bound search input model. */
  searchQuery = model<string>("");

  /** Defines which fields are included in the search */
  searchFields = input<string[]>(["postalCode", "street", "city", "description"]);

  /** Service responsible for managing property filters */
  filtersService = inject(PropertyFiltersService);

  /** Represents the currently applied search criteria. */
  appliedSearch = this.filtersService.appliedSearch;

  /**
   * Computes the number of active filters based on the current applied filters preview.
   * This is used to display a badge count on the filters button.
   */
  activeFiltersCount = computed(() => {
    return Object.keys(this.filtersService.appliedFiltersPreview() ?? {}).length;
  });

  /**
   * Applies the current search query to the appliedSearch signal.
   */
  applySearch() {
    this.filtersService.applySearch(this.searchQuery() ? { fields: this.searchFields(), value: this.searchQuery() } : undefined);
    this.searchApplied.emit(this.appliedSearch());
  }

  /**
   * Clears the search input and re-runs the search.
   * Keeps behavior consistent with manual submissions.
   */
  clearSearch() {
    this.searchQuery.set("");
    return this.applySearch();
  }
}
